import React, { useEffect, useState } from "react";
import { Search, UserPlus } from "lucide-react";
import {
  AppButton, AppDataTable, AppModal, Callout, Checkbox, FormField, Input,
} from "../../ui";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import { addStudentsOutcome } from "./batchDetails";

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

const SKIPPED_COLUMNS = [
  { key: "name", label: "Student" },
  { key: "reason", label: "Reason", render: (r) => <span className="tw-text-content-muted">{r.reason}</span> },
];

const describe = (s) => [s.mobile, s.course?.name].filter(Boolean).join(" · ");

/**
 * AddStudentsModal — find existing students and put them in this batch
 * (`POST /batches/:id/add-students`).
 *
 * A pick survives a new search, so an admin can gather people from several
 * queries and add them in one go. When the server skips some (already enrolled
 * in the course through another batch, the batch full…) the dialog stays open
 * and says who and why; a clean add closes it and the page says so.
 *
 * Props:
 *   show, onHide
 *   batchId
 *   rosterIds — ids already on screen in the roster, left out of the results
 *   terms     — useAudienceTerm()
 *   onAdded(count, message) — after anyone was added
 */
const AddStudentsModal = ({ show, onHide, batchId, rosterIds, terms, onAdded }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    if (!show) return;
    setQuery("");
    setResults([]);
    setPicked([]);
    setError("");
    setOutcome(null);
  }, [show]);

  const q = query.trim();
  useEffect(() => {
    if (q.length < MIN_QUERY) {
      setResults([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(() => {
      apiClient.get("/students/search", { params: { text: q } })
        .then(({ data }) => {
          if (cancelled) return;
          // Someone whose primary batch is this one is already here, even when
          // they are on another page of the roster.
          setResults((data.students || []).filter((s) =>
            !rosterIds.has(String(s._id)) && String(s.batch?._id ?? s.batch ?? "") !== String(batchId)));
        })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [q, batchId, rosterIds]);

  const pickedIds = new Set(picked.map((s) => String(s._id)));
  const unpicked = results.filter((s) => !pickedIds.has(String(s._id)));
  const toggle = (student) => setPicked((prev) => (
    pickedIds.has(String(student._id))
      ? prev.filter((p) => String(p._id) !== String(student._id))
      : [...prev, student]
  ));

  const submit = async () => {
    if (!picked.length) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await apiClient.post(`/batches/${batchId}/add-students`, {
        studentIds: picked.map((s) => s._id),
      });
      const result = addStudentsOutcome(data, picked);
      if (result.skipped.length) {
        setOutcome(result);
        setPicked([]);
        setQuery("");
        if (result.added > 0) onAdded(result.added);
      } else {
        onAdded(result.added, data.message || `${terms.plural} added.`);
        onHide();
      }
    } catch (err) {
      setError(err?.response?.data?.message || `Could not add ${terms.lowerPlural}.`);
    }
    setSubmitting(false);
  };

  const footer = outcome ? (
    <AppButton size="sm" onClick={onHide}>Done</AppButton>
  ) : (
    <>
      <AppButton size="sm" variant="outline" onClick={onHide} disabled={submitting}>Cancel</AppButton>
      <AppButton size="sm" onClick={submit} disabled={!picked.length || submitting}>
        {submitting
          ? "Adding…"
          : picked.length
            ? `Add ${picked.length} ${picked.length === 1 ? terms.lower : terms.lowerPlural}`
            : `Add ${terms.lowerPlural}`}
      </AppButton>
    </>
  );

  return (
    <AppModal
      show={show}
      onHide={submitting ? undefined : onHide}
      title={`Add ${terms.lowerPlural} to this batch`}
      icon={<UserPlus size={18} />}
      size="lg"
      footer={footer}
    >
      {outcome ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          {outcome.added > 0 && (
            <Callout tone="success" role="status">
              {outcome.added} {outcome.added === 1 ? terms.lower : terms.lowerPlural} added to the batch.
            </Callout>
          )}
          <Callout tone="warning" role="alert">
            {outcome.skipped.length} {outcome.skipped.length === 1 ? terms.lower : terms.lowerPlural} could not be added.
          </Callout>
          <AppDataTable
            fill={false}
            columns={SKIPPED_COLUMNS}
            rows={outcome.skipped}
            rowKey={(r) => String(r.studentId)}
          />
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-4">
          {error && <Callout tone="danger" role="alert">{error}</Callout>}

          {picked.length > 0 && (
            <section aria-label="Selected" className="tw-flex tw-flex-col tw-gap-2">
              <h3 className="tw-m-0 tw-text-xs tw-font-semibold tw-text-content-muted">
                Selected ({picked.length})
              </h3>
              <div className="tw-flex tw-flex-col tw-gap-2">
                {picked.map((s) => (
                  <Checkbox key={s._id} checked onChange={() => toggle(s)} label={s.name} description={describe(s)} />
                ))}
              </div>
            </section>
          )}

          <FormField label={`Find ${terms.lowerPlural}`} hint={`Type at least ${MIN_QUERY} letters of a name.`}>
            <Input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name…"
              leading={<Search size={16} />}
            />
          </FormField>

          <div className="tw-max-h-[300px] tw-overflow-y-auto" aria-live="polite">
            {searching ? (
              <Shimmer height={36} count={3} />
            ) : q.length >= MIN_QUERY && unpicked.length === 0 ? (
              <p className="tw-m-0 tw-py-3 tw-text-center tw-text-sm tw-text-content-subtle">
                {results.length
                  ? "Everyone found is already selected."
                  : `No ${terms.lowerPlural} match “${q}”, or they are already in this batch.`}
              </p>
            ) : (
              <div className="tw-flex tw-flex-col tw-gap-2">
                {unpicked.map((s) => (
                  <Checkbox key={s._id} checked={false} onChange={() => toggle(s)} label={s.name} description={describe(s)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AppModal>
  );
};

export default AddStudentsModal;
